import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Pagination,
  Snackbar,
  Alert,
  Typography,
  Tooltip,
  Dialog
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, DatePicker, ConfigProvider } from 'antd';
import { ConfirmationPopover } from './PopoverSystem';
import CreateExperimentForm from '../shared/CreateExperimentForm';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { useAuth } from '../../context/AuthContext';
import dayjs from 'dayjs';
import esES from 'antd/locale/es_ES';
import enUS from 'antd/locale/en_US';
import itIT from 'antd/locale/it_IT';
import { colors } from '../../themes/typography';
import { responsiveTypography, responsiveSpacing } from '../../themes/responsive';
import FilterPills from './FilterPills';
import { createFilterDropdown, FILTER_CONFIG } from './filterConfig';
// Sort icons are now centralized in themes/technicalAlerts/columns.js
import ControlBar from './ControlBar/ControlBar';
import useTechnicalAlertsFilters from '../../hooks/useTechnicalAlertsFilters';
import useTechnicalAlertsTableData from '../../hooks/useTechnicalAlertsTableData';
import useAlertActions from '../../hooks/useAlertActions';
import { useTechnicalAlertsState, useColumnConfigs } from '../../hooks/useTechnicalAlertsState';
import AlertCardsView from './AlertCardsView';
import { theme, ThemedTypography } from '../../themes/technicalAlerts';
import { createTechnicalAlertsColumns } from '../../themes/technicalAlerts/columns';
import { usePopoverState, TechnicalAlertsPopovers } from './PopoverSystem';
import KPIBox from '../shared/KPIBox';
import { TrendingDown, Warning, RateReview } from '@mui/icons-material';
import 'antd/dist/reset.css';


const TechnicalAlertsTable = ({ 
  onFiltersChange = () => {},
  onTotalsChange = () => {},
  showToast: externalShowToast
}) => {
  const { t, i18n } = useTranslation();
  const locale = { es: esES, en: enUS, it: itIT }[i18n.language] || esES;
  const navigate = useNavigate();
  
  // Centralized state management with default filter for pending alerts
  const state = useTechnicalAlertsState({
    onFiltersChange,
    externalShowToast,
    // No default filter; show all alerts by default
    initialFilters: {}
  });
  
  const { anchorEl, popoverType, popoverData, openPopover, closePopover, isOpen } = usePopoverState();
  const columnConfigs = useColumnConfigs();
  const [createConfirmOpen, setCreateConfirmOpen] = React.useState(false);
  const [createExpOpen, setCreateExpOpen] = React.useState(false);
  const [selectedRecordId, setSelectedRecordId] = React.useState(null);
  const lastAlertIdRef = React.useRef(null);
  const lastActionAnchorRef = React.useRef(null);
  const { currentUser } = useAuth();

  // Create experiment form scroll fix
  // Form is in a Dialog portal with internal scrolling.
  // Strategy: Allow modal to scroll internally, but when at edges, scroll the page behind.
  useEffect(() => {
    // Only attach when form is open
    if (!createExpOpen) return;

    const handleWheel = (e) => {
      // Find the dialog paper dynamically (it's the scrollable container)
      const paper = document.querySelector('.MuiDialog-paper');
      if (!paper || !paper.contains(e.target)) return;

      const scrollTop = paper.scrollTop;
      const scrollHeight = paper.scrollHeight;
      const clientHeight = paper.clientHeight;
      const deltaY = e.deltaY;

      // Check if scrolling up and already at top, or scrolling down and already at bottom
      const atTop = scrollTop === 0 && deltaY < 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 1 && deltaY > 0;

      // If at edge, prevent default and scroll the page instead
      if (atTop || atBottom) {
        e.preventDefault();
        e.stopPropagation();

        // Manually scroll the main page content
        const pageScrollContainer = document.querySelector('.css-4z6nar') || document.documentElement;
        pageScrollContainer.scrollTop += deltaY;
      }
      // Otherwise, let the modal scroll naturally (don't prevent default)
    };

    // Capture phase fires BEFORE target/bubble - intercepts before MUI handlers
    document.addEventListener('wheel', handleWheel, { capture: true, passive: false });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true, passive: false });
    };
  }, [createExpOpen]);

  // Hook for dynamic filter values (faceted search)
  const { filterData, loading: filtersLoading, error: filtersError } = useTechnicalAlertsFilters(state.table.filteredInfo);

  // Hook for table data with server-side operations
  const {
    data,
    loading,
    error: tableError,
    totals,
    pagination,
    setPage,
    allAlertIds,
    refetch
  } = useTechnicalAlertsTableData(state.table.filteredInfo, {
    pageSize: 10,
    initialPage: 1,
    filterData,
    sorter: state.table.sortedInfo
  });

  // Use centralized toast management
  const { showToast } = state.toast;

  // Store groups refetch function for card view
  const groupsRefetchRef = React.useRef(null);
  const groupsPreserveStateRef = React.useRef(null);
  const groupsMetadataRef = React.useRef(null);

  // Data loaded - ready for visualization
  React.useEffect(() => {
    if (data && data.length > 0) {
      // Data is ready for components to use
    }
  }, [data]);

  // Pass totals to parent when available
  React.useEffect(() => {
    onTotalsChange(totals);
  }, [totals, onTotalsChange]);

  // Use centralized toast close handler
  const { handleToastClose } = state.toast;

  // Show error toast if table data loading fails
  React.useEffect(() => {
    if (tableError) {
      showToast(t('errorLoadingAlerts'), 'error');
    }
  }, [tableError]);


  // All column rendering, sorting, and navigation logic is now centralized in themes/technicalAlerts/columns.js
  
  // Use server-provided filter values
  const filterValues = useMemo(() => {
    if (!filterData) {
      return {
        storeClusterId: [],
        machineId: [],
        productName: [],
        slots: [],
        weeksSince: [],
        routes: []
      };
    }
    return filterData;
  }, [filterData]);


  // Use centralized column configuration
  const columns = useMemo(() => createTechnicalAlertsColumns(
    state,
    filterValues,
    filtersLoading,
    navigate,
    openPopover,
    t
  ), [state, filterValues, filtersLoading, navigate, openPopover, t]);

  // Use centralized table state management
  const { handleTableChange, handleRemoveFilter, handleClearAllFilters } = state.table;


  // Handle page change - keep selection persistent across pages
  const handlePageChange = (event, page) => {
    setPage(page);
  };





  // Handle cleanup after successful date submission
  const handleDateSubmissionCleanup = (alertId) => {
    // Clear the selected date for this alert
    state.date.clearDateForRecord(alertId);
    
    // Close popover
    closePopover();
  };
  
  // Handle table date submission using shared hook
  const handleTableDateSubmit = async (alertId, selectedDate) => {
    if (!selectedDate) return;
    
    const success = await submitResolutionDate(alertId, selectedDate);
    if (success) {
      handleDateSubmissionCleanup(alertId);
    }
  };
  
  // Handle interactive table date submission using shared hook (no more double API calls)
  const handleTableDateSubmitInteractive = async (alertId, selectedDate) => {
    if (!selectedDate) return;
    
    const success = await submitResolutionDate(alertId, selectedDate);
    if (success) {
      handleDateSubmissionCleanup(alertId);
    }
  };
  
  // Handle popover actions using shared hook
  const handlePopoverAction = async (action, alertId) => {
    let success = false;
    
    if (action === 'confirm_broken') {
      success = await confirmBroken(alertId);
    } else if (action === 'confirm_not_broken') {
      success = await confirmNotBroken(alertId);
    }
    
    if (success) {
      closePopover();
    }
  };
  
  // Helper functions for popover actions
  const handleConfirmBroken = () => {
    const recordId = popoverData.recordId;
    // Transition to plan date popover (planned resolution date)
    openPopover({ currentTarget: anchorEl }, 'planDate', { recordId });
  };
  
  const handleConfirmNotBroken = async () => {
    // Preserve the current anchor, close the review popover, then open our confirm
    setSelectedRecordId(popoverData.recordId);
    lastActionAnchorRef.current = anchorEl;
    closePopover();
    setCreateConfirmOpen(true);
  };
  
  const handleSetBroken = async () => {
    // No-op for table popover "not fixed yet"; simply close
    closePopover();
  };

  const handlePlanDateSubmit = async (alertId, selectedDate) => {
    if (!selectedDate) return;
    const success = await confirmBroken(alertId, selectedDate);
    if (success) {
      handleDateSubmissionCleanup(alertId);
    }
  };



  // Use centralized view state management
  const { viewMode, groupingStrategy, handleViewModeChange, handleCardExpand, handleGroupingStrategyChange } = state.view;

  // Fetch groups metadata initially and when grouping changes to get correct counts
  React.useEffect(() => {
    if (groupsRefetchRef.current) {
      groupsRefetchRef.current();
    }
  }, [groupingStrategy, state.table.filteredInfo]);

  // View-aware refetch function that calls the appropriate refetch based on current view mode
  const viewAwareRefetch = () => {
    if (viewMode === 'table') {
      // Table view: refetch table data
      refetch();
    } else {
      // Card view: refetch groups data
      if (groupsRefetchRef.current) {
        groupsRefetchRef.current();
      }
    }
  };
  
  // Use shared alert actions hook
  const { actionLoading, submitResolutionDate, confirmBroken, confirmNotBroken } = useAlertActions({
    onSuccess: viewAwareRefetch,
    showToast,
    preserveState: () => {
      // Only preserve state for card view (where groups are used)
      if (viewMode === 'cards' && groupsPreserveStateRef.current) {
        groupsPreserveStateRef.current();
      }
    }
  });

  // State for card view pagination
  const [cardPaginationData, setCardPaginationData] = useState(null);

  // Click outside handling is now managed by the useDropdownVisibility hook

  // Card expand/collapse is now handled by state.view.handleCardExpand

  // Column configs are provided by the useColumnConfigs hook

  // Grouping strategy labels
  const groupingStrategyLabels = {
    store_cluster: 'Ubicación',
    machine: t('machine'),
    product: t('product'),
    temporal: t('time')
  };

  return (
    <Box sx={{ 
      backgroundColor: 'white',
      borderRadius: '12px',
      border: `1px solid ${colors.border.light}`,
      overflow: 'hidden'
    }}>
      {/* Title Section - exactly matching ExperimentsTable */}
      <Box sx={{ 
        px: responsiveSpacing.sm,
        pt: responsiveSpacing.lg,
        pb: 0
      }}>
            <Box sx={{
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography sx={{ 
                  fontSize: '20px', 
                  color: '#37352f',
                  fontWeight: 600,
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
                }}>
                  {t('alertsResults')}
                </Typography>
                {totals && (
                  <>
                    <Box sx={{ 
                      width: '4px', 
                      height: '4px', 
                      borderRadius: '50%', 
                      backgroundColor: '#787774',
                      alignSelf: 'center'
                    }} />
                    <Typography sx={{ 
                      fontSize: '16px', 
                      color: '#787774',
                      fontWeight: 500,
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif'
                    }}>
                      {(() => {
                        // In table view, show total alerts count
                        if (viewMode === 'table') {
                          const alertCount = totals?.totalAlerts || 0;
                          return `${alertCount} ${t('alerts_word')}`;
                        }
                        
                        // In cards view, show group count based on grouping strategy
                        const getGroupCountKey = (strategy) => {
                          switch (strategy) {
                            case 'store_cluster':
                              return 'storeClusterCount';
                            case 'machine':
                              return 'machineCount';
                            case 'product':
                              return 'productCount';
                            case 'temporal':
                              return 'temporalCount';
                            default:
                              return 'groupCount';
                          }
                        };
                        
                        const groupCount = groupsMetadataRef.current?.totalGroups || 0;
                        return t(getGroupCountKey(groupingStrategy), { count: groupCount });
                      })()}
                    </Typography>
                  </>
                )}
                <Tooltip 
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '13px' }}>
                        {t('technicalAlertsOverview')}
                      </Typography>
                      <Typography sx={{ mb: 1, fontSize: '12px' }}>
                        {t('technicalAlertsDescription')}
                      </Typography>
                      <Typography sx={{ mb: 1, fontSize: '12px' }}>
                        <strong>{t('examplesInclude')}:</strong> {t('technicalAlertsExamples')}
                      </Typography>
                      <Typography sx={{ fontSize: '12px' }}>
                        {t('technicalAlertsVsExperiments')}
                      </Typography>
                    </Box>
                  }
                  placement="bottom-start"
                  arrow
                >
                  <InfoOutlined 
                    sx={{ 
                      fontSize: '16px', 
                      color: '#8c8c8c',
                      cursor: 'help',
                      '&:hover': {
                        color: '#595959'
                      }
                    }} 
                  />
                </Tooltip>
              </Box>

              {/* View Mode Toggle - moved from ControlBar, matching problems table position */}
              {/* Commented out - only showing table view */}
              {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  display: 'flex',
                  backgroundColor: '#F9F7F4', // colors.background.light
                  borderRadius: '6px', // borderRadius.md
                  p: '2px',
                  position: 'relative'
                }}>
                  {['table', 'cards'].map((mode) => (
                    <Box
                      key={mode}
                      onClick={() => handleViewModeChange(mode)}
                      sx={{
                        position: 'relative',
                        px: '16px',
                        py: '6px',
                        cursor: 'pointer',
                        borderRadius: '4px', // borderRadius.sm
                        fontSize: '13px',
                        fontWeight: 500,
                        lineHeight: '16px',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
                        color: viewMode === mode ? '#000' : 'rgba(0, 0, 0, 0.6)',
                        backgroundColor: viewMode === mode ? '#ffffff' : 'transparent',
                        boxShadow: viewMode === mode ? '0px 1px 3px rgba(0, 0, 0, 0.04)' : 'none',
                        transition: '0.2s ease',
                        '&:hover': {
                          color: viewMode === mode ? '#000' : 'rgba(0, 0, 0, 0.8)'
                        }
                      }}
                    >
                      {mode === 'table' ? t('table') : t('cards')}
                    </Box>
                  ))}
                </Box>
              </Box> */}
            </Box>

            {/* KPI Cards - exactly matching ExperimentsTable */}
            {totals && (
              <Box sx={{
                display: 'flex',
                gap: 2,
                mt: 2,
                mb: 1,
                flexWrap: 'wrap'
              }}>
                {/* Total Estimated Loss Card */}
                <KPIBox
                  title={t('totalEstimatedLoss')}
                  value={totals.totalRevenueLoss}
                  type="currency"
                  icon={TrendingDown}
                  color="error"
                />

                {/* Total Alerts Card */}
                <KPIBox
                  title={t('totalAlerts')}
                  value={totals.totalAlerts}
                  type="units"
                  icon={Warning}
                  color="primary"
                />

                {/* Alerts Under Review Card */}
                <KPIBox
                  title={t('alertsUnderReview')}
                  value={totals.alertsUnderReview}
                  type="units"
                  icon={RateReview}
                  color="info"
                />
              </Box>
            )}

          </Box>

          {/* Control Bar */}
          <ControlBar
            state={state}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            groupingStrategy={groupingStrategy}
            groupingStrategyLabels={groupingStrategyLabels}
            onGroupingStrategyChange={handleGroupingStrategyChange}
            filterData={filterData}
            filtersLoading={filtersLoading}
            refetch={refetch}
            columnConfigs={columnConfigs}
            t={t}
          />

          {/* Content Area - Cards/Table switch */}
          <Box sx={{ px: responsiveSpacing.sm, pb: responsiveSpacing.sm }}>
            {viewMode === 'table' ? (
              <Box sx={{ 
                px: responsiveSpacing.sm,
                '& .ant-table': {
                  backgroundColor: 'transparent',
                },
                '& .ant-table-thead > tr > th': {
                  backgroundColor: 'transparent',
                  borderBottom: `1px solid ${theme.colors.border.light}`,
                  ...theme.typography.body3,
                  color: theme.colors.text.muted,
                  textTransform: 'none',
                  padding: '12px 8px',
                },
                '& .ant-table-tbody > tr > td': {
                  borderBottom: `1px solid ${theme.colors.border.light}`,
                  padding: '12px 8px',
                },
                '& .ant-table-tbody > tr:hover > td': {
                  backgroundColor: theme.colors.background.hover,
                },
                '& .ant-pagination': {
                  display: 'none', // We'll use MUI pagination instead
                },
                // Make checkbox borders darker - matching experiments table
                '& .ant-checkbox-inner': {
                  borderColor: '#666666 !important',
                },
                '& .ant-checkbox:hover .ant-checkbox-inner': {
                  borderColor: '#333333 !important',
                },
                // Keep a visible horizontal scrollbar so it's obvious the wide
                // table can be swiped on small screens (laptops/iPads/phones).
                '& .ant-table-content, & .ant-table-body': {
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': { height: '8px' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.28)', borderRadius: '4px' },
                  '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.06)' },
                },
              }}>
                <Table
                  columns={columns}
                  dataSource={data}
                  rowKey="id"
                  loading={loading}
                  pagination={false}
                  size="small"
                  scroll={{ x: 1000 }}
                  tableLayout="fixed"
                  onChange={handleTableChange}
                  onRow={(record) => ({
                    onMouseEnter: () => state.table.setHoveredRowKey(record.id),
                    onMouseLeave: () => state.table.setHoveredRowKey(null),
                  })}
                />
              </Box>
            ) : (
              // Cards View with expandable height container
              <Box 
                data-scroll-container
                sx={{ 
                  minHeight: '60vh',
                  overflow: 'visible',
                  paddingBottom: '40px'
                }}>
                <AlertCardsView
                  filters={state.table.filteredInfo}
                  loading={loading}
                  selectedRowKeys={state.selection?.selectedRowKeys || []}
                  onSelectionChange={state.selection?.handleSelectionChange}
                  expandedCards={state.view.expandedCards}
                  onCardExpand={state.view.handleCardExpand}
                  expandedGroups={state.view.expandedGroups}
                  onGroupToggle={state.view.handleGroupToggle}
                  groupingStrategy={groupingStrategy}
                  onRefetch={(refetchFn, preserveStateFn, metadata) => { 
                    groupsRefetchRef.current = refetchFn; 
                    groupsPreserveStateRef.current = preserveStateFn;
                    groupsMetadataRef.current = metadata;
                  }}
                  onPaginationChange={setCardPaginationData}
                  actionLoading={actionLoading}
                  submitResolutionDate={submitResolutionDate}
                  confirmBroken={confirmBroken}
                  confirmNotBroken={confirmNotBroken}
                  showToast={showToast}
                />
              </Box>
            )}
          </Box>

          {/* Conditional Pagination */}
      {viewMode === 'table' ? (
        pagination.totalPages > 1 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            pt: 3,
            px: responsiveSpacing.xs
          }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={handlePageChange}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPagination-ul': {
                  justifyContent: 'center'
                },
                '& .MuiPaginationItem-root': {
                  ...theme.typography.body1
                }
              }}
            />
          </Box>
        )
      ) : (
        cardPaginationData && cardPaginationData.pagination.totalPages > 1 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            pt: 3,
            px: responsiveSpacing.xs
          }}>
            <Pagination
              count={cardPaginationData.pagination.totalPages}
              page={cardPaginationData.page}
              onChange={cardPaginationData.handlePageChange}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPagination-ul': {
                  justifyContent: 'center'
                },
                '& .MuiPaginationItem-root': {
                  ...theme.typography.body1
                }
              }}
            />
          </Box>
        )
      )}

      {/* Toast Notifications - only show if no external showToast provided */}
      {!externalShowToast && (
        <Snackbar
          open={state.toast.toastOpen}
          autoHideDuration={4000}
          onClose={state.toast.handleToastClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={state.toast.handleToastClose}
            severity={state.toast.toastSeverity}
            variant="filled"
            sx={{ 
              ...theme.typography.body1
            }}
          >
            {state.toast.toastMessage}
          </Alert>
        </Snackbar>
      )}


      {/* Review Popover */}
      <TechnicalAlertsPopovers.Review
        open={isOpen('review')}
        anchorEl={anchorEl}
        onClose={closePopover}
        onConfirmBroken={handleConfirmBroken}
        onConfirmNotBroken={handleConfirmNotBroken}
      />

      {/* Create Experiment? confirmation (after choosing Not Broken) */}
      <ConfirmationPopover
        open={createConfirmOpen}
        anchorEl={lastActionAnchorRef.current}
        onClose={() => setCreateConfirmOpen(false)}
        title="createExperimentQuestion"
        onConfirm={() => {
          setCreateConfirmOpen(false);
          setCreateExpOpen(true);
        }}
        onCancel={async () => {
          setCreateConfirmOpen(false);
          if (selectedRecordId) {
            await handlePopoverAction('confirm_not_broken', selectedRecordId);
          }
        }}
        confirmText="yes"
        cancelText="no"
      />

      {/* Create Experiment Dialog */}
      <Dialog
        open={createExpOpen}
        onClose={() => setCreateExpOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: `1px solid ${colors.border.light}`,
            boxShadow: `0 8px 32px rgba(0,0,0,0.08)`,
            backdropFilter: 'blur(16px)',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            maxHeight: 'calc(100vh - 100px)',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
            <CreateExperimentForm
          open={createExpOpen}
          column={{ id: 'pending' }}
          currentFilters={{}}
          filterOptions={{ groups: [] }}
          clientId={currentUser?.customer_id}
          allowPriceIncrease={false}
          onClose={() => setCreateExpOpen(false)}
          onSuccess={async () => {
            await viewAwareRefetch();
            setCreateExpOpen(false);
            closePopover();
          }}
          onError={(message) => showToast(message, 'error')}
          t={t}
          beforeSubmit={async () => {
            const token = localStorage.getItem('access_token');
            const resp = await axios.post(
              `${API_BASE_URL}/alerts/update?clientId=${currentUser?.customer_id}`,
              { alertIds: [selectedRecordId], action: 'confirm_not_broken', metadata: {} },
              { headers: createAuthHeader(token) }
            );
            const succ = resp.data?.results?.succeeded || [];
            lastAlertIdRef.current = (succ[0]?.alertId) ?? selectedRecordId;
          }}
          extraRequestBody={{ alertId: lastAlertIdRef.current ?? selectedRecordId }}
          initialValues={(() => {
            const record = data.find(r => r.id === selectedRecordId) || {};
            return {
              experimentType: 'reemplazo_producto',
              dStoreId: record.d_store_id || record.machine_id,
              dProductId: record.d_product_id,
              productName: record.product_name,
              machineDisplayName: `${record.group || ''}${record.group ? ' - ' : ''}${record.location || ''}${record.machine_id ? ` (${record.machine_id})` : ''}`.trim()
            };
          })()}
          lockMachine
          lockProduct
          presetSelection={(() => {
            const record = data.find(r => r.id === selectedRecordId) || {};
            return {
              dStoreId: record.d_store_id || record.machine_id,
              dProductId: record.d_product_id,
              machineLabel: `${record.group || ''}${record.group ? ' - ' : ''}${record.location || ''}${record.machine_id ? ` (${record.machine_id})` : ''}`.trim(),
              productLabel: record.product_name
            };
          })()}
        />
      </Dialog>

      {/* Plan Date Popover - after confirming broken (pending → under_review) */}
      <TechnicalAlertsPopovers.PlanDate
        open={isOpen('planDate')}
        anchorEl={anchorEl}
        onClose={closePopover}
        selectedDate={state.date.getDateForRecord(popoverData.recordId) || dayjs().add(1,'day')}
        onDateChange={(date) => {
          state.date.setDateForRecord(popoverData.recordId, date || dayjs().add(1,'day'));
        }}
        onSubmit={() => handlePlanDateSubmit(popoverData.recordId, state.date.getDateForRecord(popoverData.recordId) || dayjs().add(1,'day'))}
        loading={actionLoading === 'confirm_broken'}
      />

      {/* Fix Date Popover - under_review → resolved */}
      <TechnicalAlertsPopovers.FixDate
        open={isOpen('fixDate')}
        anchorEl={anchorEl}
        onClose={closePopover}
        selectedDate={(() => {
          // Use API's resolution date as default, or today if not available
          const recordId = popoverData.recordId;
          const storedDate = state.date.getDateForRecord(recordId);
          if (storedDate) return storedDate;
          
          // Find the record and use its resolution_date
          const record = data.find(item => item.id === recordId);
          if (record?.resolution_date) {
            return dayjs(record.resolution_date);
          }
          return dayjs(); // Fallback to today
        })()}
        onDateChange={(date) => {
          state.date.setDateForRecord(popoverData.recordId, date || dayjs());
        }}
        onSubmit={() => handleTableDateSubmitInteractive(popoverData.recordId, state.date.getDateForRecord(popoverData.recordId) || dayjs())}
        loading={actionLoading === 'set_resolution_date'}
      />

    </Box>
  );
};

export default TechnicalAlertsTable;
